/**
 * Thin wrapper around `vite-plugin-pwa`'s virtual `registerSW` module.
 *
 * The virtual module (`virtual:pwa-register`) is only resolved by Vite at
 * build/dev time when the PWA plugin is active. In other environments
 * (the Vitest/jsdom test runner, SSR, etc.) the import simply fails — we
 * swallow that and treat the SW as a no-op so callers never have to care.
 *
 * Callers should invoke `setupPWA()` once at app bootstrap (see `main.tsx`).
 * A real upgrade-prompt UX is deferred to Wave 4; for now we just log when a
 * new version is ready to install.
 */

export type RegisterSWOptions = {
  /** Called when a new SW has installed and is waiting to activate. */
  onNeedRefresh?: () => void;
  /** Called when the SW reports the app is ready to work offline. */
  onOfflineReady?: () => void;
  /** Called for unexpected registration errors (network, parse, etc.). */
  onRegisterError?: (err: unknown) => void;
};

export type UpdateSW = (reloadPage?: boolean) => Promise<void>;

const noop = () => {};
const noopUpdate: UpdateSW = async () => {};

/**
 * Register the service worker. Returns an `updateSW` function that, when
 * called with `true`, reloads the page after activating a new SW. If the
 * PWA virtual module is unavailable (e.g. tests), returns a no-op.
 */
export async function setupPWA(options: RegisterSWOptions = {}): Promise<UpdateSW> {
  const {
    onNeedRefresh = () => console.info('[pwa] new content available; reload to update'),
    onOfflineReady = noop,
    onRegisterError = (err) => console.warn('[pwa] sw registration failed', err),
  } = options;

  // Guard: navigator may be undefined in non-browser contexts (SSR, tests).
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return noopUpdate;
  }

  try {
    // Static specifier so Vite/vite-plugin-pwa replaces the virtual module
    // with the real registration code at build time. A computed string with
    // `@vite-ignore` (the previous approach) leaks the literal
    // `virtual:pwa-register` into the bundle, where the browser fetches it as
    // a URL and fails — meaning the SW never registered in production. Under
    // Vitest the plugin isn't active, so this import rejects and the catch
    // below falls through to the no-op (see pwa.test.ts).
    const mod = (await import('virtual:pwa-register')) as {
      registerSW: (opts: {
        immediate?: boolean;
        onNeedReload?: () => void;
        onNeedRefresh?: () => void;
        onOfflineReady?: () => void;
        onRegisterError?: (err: unknown) => void;
        onRegisteredSW?: (
          swScriptUrl: string,
          registration: ServiceWorkerRegistration | undefined,
        ) => void;
      }) => UpdateSW;
    };

    const updateSW = mod.registerSW({
      immediate: true,
      // Page loads are network-first, so the page already runs the new build by
      // the time its SW activates. The plugin's default reload on activation
      // would load it a second time. The deferred controllerchange reload below
      // covers a page that came from the offline shell.
      onNeedReload: noop,
      onNeedRefresh,
      onOfflineReady,
      onRegisterError,
      onRegisteredSW(_swScriptUrl, registration) {
        if (!registration) return;
        // A home-screen PWA reopened days later (the weekly-market use case)
        // may not re-request the SW script on its own, so it keeps serving a
        // stale precached build. Re-check for a new SW whenever the app
        // becomes visible; with registerType 'autoUpdate' a found update
        // activates and reloads to the latest version.
        //
        // `update()` rejects on the routine churn of this app's environment —
        // deploy races (fetching the new sw.js mid-rollout) and flaky mobile
        // networks. Route those to onRegisterError instead of leaking an
        // unhandled rejection that PostHog autocaptures as noise.
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            registration.update().catch(onRegisterError);
          }
        });
      },
    });

    // After a deploy the new SW activates with skipWaiting + clientsClaim
    // (registerType 'autoUpdate' defaults), but the page that triggered the
    // update is already rendered from the old precache and nothing reloads it.
    // controllerchange fires when the new SW takes control — reload then so
    // users see fresh content without a manual hard refresh.
    // Only attach when there is already a controller: on a first visit the
    // initial controllerchange is the brand-new SW first claiming the page,
    // and reloading there would loop.
    if (navigator.serviceWorker.controller) {
      let reloading = false;
      const reload = () => {
        if (reloading) return;
        reloading = true;
        window.location.reload();
      };
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        // Defer the reload while the tab is visible so we don't yank the page
        // out from under an actively-engaged user (e.g. mid-edit in Editor
        // mode). When the tab next goes hidden, reload — by the time they
        // come back, the new version is loaded.
        if (document.visibilityState !== 'visible') {
          reload();
          return;
        }
        const onVisibilityChange = () => {
          if (document.visibilityState !== 'visible') {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            reload();
          }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
      });
    }

    return updateSW ?? noopUpdate;
  } catch {
    // PWA plugin not active (tests, SSR, etc.) — silently fall through.
    return noopUpdate;
  }
}
